/** Guest journey → Pre-trip items (plan §2 S5, D7, D12): the tour's checklist as draft
 *  rows (`pretrip:<id>`), with the membership list `pre_trip_items.order`.
 *
 *  - Add an item from the operator's catalog, or **import from another tour** (its items
 *    that are not here yet, visible to all users — the dashboard's import) — one undo step
 *    each; reorder; remove one, or tick several and **remove them in one step**; restore a
 *    stored row until the save; `required` per row.
 *  - **Visible to** (only while the operator uses pre-trip visibility,
 *    `enable_pre_trip_items_visibility`): All users / All travelers / roles / access
 *    groups / individual users booked on the tour (fixed-date tours only), in the row's
 *    details. A row the draft adds or retargets must be visible to someone — else an
 *    invalid input on the row blocks Save (the dashboard's "Every trip item must be
 *    visible"); a row stored that way only says so.
 *  - The table fits the panel (≈720 px): select, item, required, visible to, actions; the
 *    targeting is the details row.
 *  - `use_group_tour_pre_trip_items` off: the tour uses its itinerary's (legacy) items — a
 *    per-tour setting fixed when the tour was created; no table.
 *
 *  The block is `tour#pre-trip-items` (the old tab slug lands here). Edit-mode controls sit
 *  in the heading row, reserved in both modes; the select column is reserved too. */
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import * as toursApi from '@/api/tours';
import type { PickerOption, TourListItem } from '@/api/tours';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { PickerLinks } from '@/components/ValueActions/PickerLinks';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { addRow, addRows, moveRow, removeRow, removeRows, restoreRow, tableRows } from '@/features/records/collections/rows';
import { DraftCell, RowIssues } from '@/features/records/fields/DraftCell';
import { FIELD_GRID_SX, FIELD_GRID_VAR } from '@/features/records/panels/fieldGrid';
import { recordKey, type RecordKey } from '@/features/records/draft/changeSet';
import { useDraft } from '@/features/records/draft/DraftProvider';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import { CHANGED_TINT } from '@/features/records/fields/FieldSlot';
import { FieldCellGrid } from '@/features/records/fields/FieldCellGrid';
import { FieldSlot } from '@/features/records/fields/FieldSlot';
import { HEADING_ROW_PX } from '@/features/records/panels/headingRow';

import { accessGroupActions, preTripItemActions, visibleUserActions } from '../../tourLinks';
import { PRETRIP_ENTITY, TOUR_ENTITY } from '../catalog';
import { COLLECTION_BY_ENTITY } from '../collections';
import { useTourRecord } from '../TourRecordContext';

import { BlockAnchor } from './BlockAnchor';

const PRETRIPS = COLLECTION_BY_ENTITY.get(PRETRIP_ENTITY)!;
const MEMBERSHIP = PRETRIPS.membership;
const VISIBILITY = ['pretrip.all_users', 'pretrip.all_travelers', 'pretrip.roles', 'pretrip.agroups', 'pretrip.users'] as const;
export const INVISIBLE = 'Visible to nobody — pick who sees it';
/** The invalid-input key of a row's targeting as a whole. */
const VISIBLE_TO = 'pretrip.visible_to';

const HEAD_SX = { py: 0.5, px: 0.75, fontSize: 12, color: 'text.secondary', fontWeight: 600, whiteSpace: 'nowrap' } as const;
const CELL_SX = { py: 0.25, px: 0.5, verticalAlign: 'middle' } as const;

/** Whether a row's targeting reaches someone. */
export function visibleToSomeone(valueOf: (path: string) => unknown): boolean {
  if (valueOf('pretrip.all_users') === true || valueOf('pretrip.all_travelers') === true) return true;
  return ['pretrip.roles', 'pretrip.agroups', 'pretrip.users'].some((p) => {
    const v = valueOf(p);
    return Array.isArray(v) && v.length > 0;
  });
}

export function PreTripItemsBlock() {
  const { detail, names } = useTourRecord();
  const { mode, registerFocus, errorsFor } = useRecordEditor();
  const { state, dispatch, baselineOf } = useDraft();
  const editing = mode === 'edit';
  const tourRec = recordKey(TOUR_ENTITY, detail.id);
  const canChange = editing && Boolean(detail.tenant) && !detail.is_deleted;
  const visibilityOn = detail.enable_pre_trip_items_visibility;
  const rows = tableRows(state, baselineOf, tourRec, PRETRIPS, editing);
  const live = rows.filter((r) => !r.removed);
  const [open, setOpen] = useState<Set<number>>(() => new Set());
  const [selected, setSelected] = useState<Set<number>>(() => new Set());
  const [importAnchor, setImportAnchor] = useState<HTMLElement | null>(null);
  const toggle = (id: number, to?: boolean) =>
    setOpen((cur) => {
      const next = new Set(cur);
      if (to ?? !next.has(id)) next.add(id);
      else next.delete(id);
      return next;
    });

  // Leaving edit mode, or a row leaving the list, drops it from the selection.
  const liveKey = live.map((r) => r.id).join(',');
  useEffect(() => {
    setSelected((cur) => new Set([...cur].filter((id) => editing && live.some((r) => r.id === id))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, liveKey]);

  useEffect(
    () =>
      registerFocus(tourRec, MEMBERSHIP, () => {
        const el = document.getElementById('tour-block-pre-trip-items');
        el?.scrollIntoView?.({ block: 'start' });
        el?.focus({ preventScroll: true });
      }),
    [registerFocus, tourRec],
  );

  const valueOf = (rec: RecordKey, path: string): unknown => {
    const e = state.entries[rec]?.[path];
    if (e && editing) return e.to;
    const b = baselineOf(rec, path);
    return b === undefined ? PRETRIPS.defaults[path] : b;
  };
  const itemOf = (id: number) => valueOf(recordKey(PRETRIP_ENTITY, id), 'pretrip.item');
  const nameOfRow = (id: number) => names.itemLabel(itemOf(id));
  const present = new Set(live.map((r) => itemOf(r.id)));

  const addItem = (o: PickerOption) => {
    names.rememberItem({ id: o.id, name: o.name });
    const { action } = addRow(state, baselineOf, tourRec, PRETRIPS, {
      seed: { 'pretrip.item': o.id },
      label: `Add the ${o.name ?? `#${o.id}`} pre-trip item`,
    });
    dispatch(action);
  };
  const removeSelected = () => {
    const ids = [...selected];
    dispatch(removeRows(state, baselineOf, tourRec, PRETRIPS, ids, `Remove ${ids.length} pre-trip ${ids.length === 1 ? 'item' : 'items'}`));
    setSelected(new Set());
  };
  const membershipErrors = editing ? errorsFor(tourRec, MEMBERSHIP) : [];

  if (!detail.use_group_tour_pre_trip_items) {
    return (
      <BlockAnchor id="pre-trip-items" label="Pre-trip items">
        <Box sx={{ display: 'grid', gap: 0.5 }}>
          <Typography variant="caption" component="h3" color="text.secondary" sx={{ m: 0, fontWeight: 600 }}>
            Pre-trip items
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }} data-pretrip-legacy>
            <InfoOutlinedIcon aria-hidden sx={{ fontSize: 16, mt: '2px' }} />
            This tour uses its itinerary’s pre-trip items. Whether a tour has items of its own is fixed when the tour is
            created (the operator’s enable_group_tour_pre_trip_items at that moment) and cannot be changed later.
          </Typography>
        </Box>
      </BlockAnchor>
    );
  }

  const cols = visibilityOn ? 5 : 4;
  return (
    <BlockAnchor id="pre-trip-items" label="Pre-trip items">
      <Box sx={{ display: 'grid', gap: 0.75 }}>
        <Box data-block-heading sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: HEADING_ROW_PX, minWidth: 0 }}>
          <Typography variant="caption" component="h3" color="text.secondary" noWrap sx={{ m: 0, fontWeight: 600, minWidth: 0 }}>
            Pre-trip items
            <Box component="span" sx={{ fontWeight: 400 }}>
              {' '}
              · {live.length}
            </Box>
          </Typography>
          <Box sx={{ flex: 1 }} />
          {canChange && selected.size > 0 && (
            <Button size="small" color="error" startIcon={<DeleteSweepOutlinedIcon />} onClick={removeSelected}>
              Remove selected ({selected.size})
            </Button>
          )}
          {canChange && (
            <Button size="small" startIcon={<FileDownloadOutlinedIcon />} onClick={(e) => setImportAnchor(e.currentTarget)}>
              Import from a tour
            </Button>
          )}
          {canChange && (
            <Box sx={{ width: 220, flex: 'none' }}>
              <RemoteAutocomplete<PickerOption>
                label="Add an item"
                errorText="Couldn’t load the operator’s pre-trip items"
                size="small"
                fetchOnEmpty
                value={null}
                getOptionLabel={(o) => o.name ?? `#${o.id}`}
                getOptionKey={(o) => o.id}
                getOptionMeta={(o) => ({ name: o.name ?? `#${o.id}`, pk: o.id, tenant: detail.tenant?.code ?? null })}
                queryFn={async (q) => {
                  const r = await toursApi.picker(detail.tenant!.id, 'pre_trip_items', q);
                  return r.results.filter((o) => !present.has(o.id));
                }}
                onChange={(v) => {
                  const o = v as PickerOption | null;
                  if (o) addItem(o);
                }}
              />
            </Box>
          )}
        </Box>
        {importAnchor && (
          <ImportFromTour
            anchor={importAnchor}
            onClose={() => setImportAnchor(null)}
            present={present}
            onImport={(items, from) => {
              for (const it of items) names.rememberItem(it);
              const seeds = items.map((it) => ({ 'pretrip.item': it.id }));
              const { action } = addRows(state, baselineOf, tourRec, PRETRIPS, seeds, `Import ${items.length} pre-trip ${items.length === 1 ? 'item' : 'items'} from ${from}`);
              dispatch(action);
              setImportAnchor(null);
            }}
          />
        )}
        <Box sx={{ overflowX: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 560 }} aria-label="Pre-trip items" data-pretrip-table>
            <colgroup>
              <col style={{ width: '6%' }} />
              <col style={{ width: visibilityOn ? '34%' : '58%' }} />
              <col style={{ width: '14%' }} />
              {visibilityOn && <col style={{ width: '30%' }} />}
              <col style={{ width: visibilityOn ? '16%' : '22%' }} />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell sx={HEAD_SX}>
                  <Box component="span" sx={visuallyHidden}>
                    Select
                  </Box>
                </TableCell>
                <TableCell sx={HEAD_SX}>Item</TableCell>
                <TableCell sx={HEAD_SX}>Required</TableCell>
                {visibilityOn && <TableCell sx={HEAD_SX}>Visible to</TableCell>}
                <TableCell sx={HEAD_SX}>
                  <Box component="span" sx={visuallyHidden}>
                    Row actions
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={cols} sx={{ py: 1, color: 'text.secondary' }}>
                    No pre-trip items.
                  </TableCell>
                </TableRow>
              )}
              {rows.map(({ id, removed }) => {
                const index = live.findIndex((r) => r.id === id);
                return (
                  <PreTripRow
                    key={id}
                    id={id}
                    name={nameOfRow(id)}
                    removed={removed}
                    first={index === 0}
                    last={index === live.length - 1}
                    canChange={canChange}
                    visibilityOn={visibilityOn}
                    cols={cols}
                    open={open.has(id)}
                    onToggle={(to) => toggle(id, to)}
                    selected={selected.has(id)}
                    onSelect={(on) =>
                      setSelected((cur) => {
                        const next = new Set(cur);
                        if (on) next.add(id);
                        else next.delete(id);
                        return next;
                      })
                    }
                    valueOf={valueOf}
                    onMove={(delta) => {
                      const a = moveRow(state, baselineOf, tourRec, PRETRIPS, id, delta, `Move ${nameOfRow(id)} ${delta < 0 ? 'up' : 'down'}`);
                      if (a) dispatch(a);
                    }}
                    onRemove={() => dispatch(removeRow(state, baselineOf, tourRec, PRETRIPS, id, `Remove ${nameOfRow(id)}`))}
                    onRestore={() => dispatch(restoreRow(state, baselineOf, tourRec, PRETRIPS, id, `Restore ${nameOfRow(id)}`))}
                  />
                );
              })}
            </TableBody>
          </Table>
        </Box>
        {membershipErrors.map((p) => (
          <Typography key={p} variant="caption" color="error" sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }} data-row-issue="invalid">
            <ErrorOutlineIcon aria-hidden sx={{ fontSize: 16 }} />
            {p}
          </Typography>
        ))}
      </Box>
    </BlockAnchor>
  );
}

function PreTripRow({
  id,
  name,
  removed,
  first,
  last,
  canChange,
  visibilityOn,
  cols,
  open,
  onToggle,
  selected,
  onSelect,
  valueOf,
  onMove,
  onRemove,
  onRestore,
}: {
  id: number;
  name: string;
  removed: boolean;
  first: boolean;
  last: boolean;
  canChange: boolean;
  visibilityOn: boolean;
  cols: number;
  open: boolean;
  onToggle: (to?: boolean) => void;
  selected: boolean;
  onSelect: (on: boolean) => void;
  valueOf: (rec: RecordKey, path: string) => unknown;
  onMove: (delta: -1 | 1) => void;
  onRemove: () => void;
  onRestore: () => void;
}) {
  const { detail, links } = useTourRecord();
  const { mode, registerFocus, setInvalidInput, optionRef } = useRecordEditor();
  const { state } = useDraft();
  const editing = mode === 'edit';
  const rec = recordKey(PRETRIP_ENTITY, id);
  const v = (path: string) => valueOf(rec, path);
  const item = v('pretrip.item');

  // Visible to nobody: a row the draft adds or retargets blocks Save; one stored like
  // that only says so.
  const visible = visibleToSomeone(v);
  const retargeted = id < 0 || VISIBILITY.some((p) => state.entries[rec]?.[p]);
  const invalid = editing && visibilityOn && !removed && !visible && retargeted ? INVISIBLE : null;
  // Filed under a key of its own (the row's targeting as a whole): a cell's own effect
  // would clear it.
  useEffect(() => {
    setInvalidInput(rec, VISIBLE_TO, invalid);
  }, [invalid, rec, setInvalidInput]);
  useEffect(() => () => setInvalidInput(rec, VISIBLE_TO, null), [rec, setInvalidInput]);
  useEffect(
    () =>
      registerFocus(rec, VISIBLE_TO, () => {
        onToggle(true);
        requestAnimationFrame(() => document.querySelector<HTMLElement>(`[data-pretrip-details="${id}"] input`)?.focus());
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [registerFocus, rec],
  );

  // A ledger row for a targeting field opens the details first.
  useEffect(() => {
    const offs = VISIBILITY.map((path) =>
      registerFocus(rec, path, () => {
        onToggle(true);
        requestAnimationFrame(() =>
          document.querySelector<HTMLElement>(`[data-pretrip-details="${id}"] [data-field-slot="${path}"] input, [data-pretrip-details="${id}"] [data-field-slot="${path}"]`)?.focus(),
        );
      }),
    );
    return () => offs.forEach((off) => off());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerFocus, rec, open]);

  const summary = (() => {
    if (v('pretrip.all_users') === true) return 'All users';
    const parts: string[] = [];
    if (v('pretrip.all_travelers') === true) parts.push('All travelers');
    for (const [path, noun] of [
      ['pretrip.roles', 'role'],
      ['pretrip.agroups', 'access group'],
      ['pretrip.users', 'user'],
    ] as const) {
      const ids = Array.isArray(v(path)) ? (v(path) as number[]) : [];
      if (ids.length === 1) parts.push(`${optionRef(path, ids[0])?.label ?? `1 ${noun}`}`);
      else if (ids.length > 1) parts.push(`${ids.length} ${noun}s`);
    }
    return parts.length > 0 ? parts.join(' · ') : 'Nobody';
  })();
  const struck = removed ? { textDecoration: 'line-through' } : {};
  /** A stored row's access groups / users with their names (the detail's). */
  const storedRefs = (rowId: number, kind: 'agroups' | 'users') =>
    (detail.pre_trip_items.find((r) => r.id === rowId)?.[kind] ?? []) as Array<{ id: number; name: string | null }>;
  const cells = [
    { rec, path: 'pretrip.required', label: 'Required' },
    ...VISIBILITY.map((path) => ({ rec, path, label: path.replace('pretrip.', '').replace('_', ' ') })),
  ];

  return (
    <>
      <TableRow data-pretrip-row={id} data-removed={removed || undefined} sx={[removed && { bgcolor: CHANGED_TINT }]}>
        <TableCell sx={CELL_SX}>
          {canChange && !removed && (
            <Checkbox
              size="small"
              checked={selected}
              onChange={(e) => onSelect(e.target.checked)}
              inputProps={{ 'aria-label': `Select ${name}` }}
              sx={{ p: 0.5 }}
            />
          )}
        </TableCell>
        <TableCell sx={{ ...CELL_SX, ...struck }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            {typeof item === 'number' ? (
              <ValueActions actions={preTripItemActions({ id: item }, links)}>
                <Typography variant="body2" noWrap>
                  {name}
                </Typography>
              </ValueActions>
            ) : (
              <Typography variant="body2" noWrap>
                {name}
              </Typography>
            )}
            {id < 0 && editing && <Chip size="small" label="new" variant="outlined" sx={{ height: 18, fontSize: 11 }} />}
          </Box>
        </TableCell>
        <TableCell sx={{ ...CELL_SX, ...struck }}>
          <DraftCell rec={rec} path="pretrip.required" label="Required" rowName={name} display={(x) => (x === true ? 'Required' : 'Optional')} />
        </TableCell>
        {visibilityOn && (
          <TableCell sx={{ ...CELL_SX, ...struck }}>
            {/* The flex on the outer box, the ellipsis on the text (a flex Typography never
                ellipsises). */}
            <Box title={summary} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }} data-visible-to>
              {!visible && !removed && (
                <WarningAmberIcon aria-hidden sx={{ fontSize: 16, flex: 'none', color: invalid ? 'error.main' : 'warning.main' }} />
              )}
              <Typography component="span" variant="body2" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {summary}
              </Typography>
            </Box>
          </TableCell>
        )}
        <TableCell sx={{ ...CELL_SX, textAlign: 'right', whiteSpace: 'nowrap' }}>
          {visibilityOn && !removed && (
            <Tooltip title={open ? 'Hide who sees it' : 'Who sees it'}>
              <IconButton size="small" aria-expanded={open} aria-label={`Who sees ${name}`} onClick={() => onToggle()}>
                {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}
          {canChange && !removed && (
            <>
              <IconButton size="small" aria-label={`Move ${name} up`} disabled={first} onClick={() => onMove(-1)}>
                <ArrowUpwardIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton size="small" aria-label={`Move ${name} down`} disabled={last} onClick={() => onMove(1)}>
                <ArrowDownwardIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <Tooltip title={id < 0 ? 'Remove this new item' : 'Remove — deleted on save'}>
                <IconButton size="small" aria-label={`Remove ${name}`} onClick={onRemove}>
                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </>
          )}
          {canChange && removed && (
            <Tooltip title="Restore — keep this item">
              <IconButton size="small" aria-label={`Restore ${name}`} onClick={onRestore}>
                <RestoreIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
      {removed && editing && (
        <TableRow>
          <TableCell colSpan={cols} sx={{ py: 0.25, px: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <RemoveCircleOutlineIcon aria-hidden sx={{ fontSize: 14, color: 'warning.main' }} />
              {name} is removed from the tour when you save — Restore keeps it.
            </Typography>
          </TableCell>
        </TableRow>
      )}
      {!removed && (
        <TableRow>
          <TableCell colSpan={cols} sx={{ py: 0, px: 1, borderBottom: 0, '&:empty': { p: 0 } }}>
            {invalid && (
              <Typography variant="caption" color="error" sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }} data-row-issue="invalid">
                <ErrorOutlineIcon aria-hidden sx={{ fontSize: 16 }} />
                {name}: {INVISIBLE}
              </Typography>
            )}
            {!invalid && visibilityOn && !visible && (
              <Typography variant="caption" color="warning.dark" sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }} data-row-issue="warning">
                <WarningAmberIcon aria-hidden sx={{ fontSize: 16 }} />
                {name}: visible to nobody (stored like this — saving other changes is fine)
              </Typography>
            )}
            <RowIssues cells={cells} />
          </TableCell>
        </TableRow>
      )}
      {open && !removed && visibilityOn && (
        <TableRow data-pretrip-details={id}>
          {/* The record's field grid (232 px columns, 16 px gutters) for the five targets,
              counted for this cell's own width; the two "everyone" switches are one cell. */}
          <TableCell colSpan={cols} sx={{ px: 1.5, py: 1, bgcolor: 'background.default', [FIELD_GRID_VAR]: `repeat(auto-fill, minmax(232px, 1fr))` }}>
            <FieldCellGrid
              sx={FIELD_GRID_SX}
              cells={[
                {
                  key: `${rec}:everyone`,
                  rec,
                  path: 'pretrip.all_users',
                  wide: false,
                  node: (
                    <Box sx={{ display: 'grid', alignContent: 'start' }} data-pretrip-everyone>
                      <Typography variant="caption" color="text.secondary" sx={{ minHeight: 24, display: 'flex', alignItems: 'center' }}>
                        Everyone
                      </Typography>
                      <DraftCell rec={rec} path="pretrip.all_users" label="All users" rowName={name} showLabel />
                      <DraftCell rec={rec} path="pretrip.all_travelers" label="All travelers" rowName={name} showLabel />
                    </Box>
                  ),
                },
                {
                  key: `${rec}:roles`,
                  rec,
                  path: 'pretrip.roles',
                  wide: false,
                  node: <FieldSlot fieldKey="pretrip.roles" entity={PRETRIP_ENTITY} id={id} label="Roles (legacy)" />,
                },
                {
                  key: `${rec}:agroups`,
                  rec,
                  path: 'pretrip.agroups',
                  wide: false,
                  node: <FieldSlot fieldKey="pretrip.agroups" entity={PRETRIP_ENTITY} id={id} label="Access groups" />,
                },
                {
                  key: `${rec}:users`,
                  rec,
                  path: 'pretrip.users',
                  wide: false,
                  node: (
                    <FieldSlot
                      fieldKey="pretrip.users"
                      entity={PRETRIP_ENTITY}
                      id={id}
                      label="Users on this tour"
                      // A recurring tour targets no individual users: the reason in place of
                      // an empty value (the slot is read-only).
                      {...(!detail.is_bespoke && storedRefs(id, 'users').length === 0
                        ? { display: 'Fixed-date tours only — a recurring tour has no individual users to target' }
                        : {})}
                    />
                  ),
                },
              ]}
            />
            {/* The stored targets, each reachable (open the group or the user). */}
            <PickerLinks
              prefix="Open:"
              items={[
                ...storedRefs(id, 'agroups').map((g) => ({ key: `ag-${g.id}`, label: g.name ?? `Group #${g.id}`, actions: accessGroupActions(g) })),
                ...storedRefs(id, 'users').map((u) => ({ key: `u-${u.id}`, label: u.name ?? `User #${u.id}`, actions: visibleUserActions(u) })),
              ]}
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

/** Import from another tour: pick a tour of the operator; its items not here yet are
 *  added (visible to all users), in one step. */
function ImportFromTour({
  anchor,
  onClose,
  present,
  onImport,
}: {
  anchor: HTMLElement;
  onClose: () => void;
  present: ReadonlySet<unknown>;
  onImport: (items: Array<{ id: number; name: string | null }>, fromName: string) => void;
}) {
  const { detail } = useTourRecord();
  const [picked, setPicked] = useState<TourListItem | null>(null);
  const [state, setState] = useState<{ loading: boolean; error: string | null; none: boolean }>({ loading: false, error: null, none: false });

  const run = async () => {
    if (!picked) return;
    setState({ loading: true, error: null, none: false });
    try {
      const other = await toursApi.retrieve(picked.id);
      const seen = new Set<number>();
      const items = other.pre_trip_items
        .map((r) => r.item)
        .filter((it): it is { id: number; name: string | null } => {
          if (!it || present.has(it.id) || seen.has(it.id)) return false;
          seen.add(it.id);
          return true;
        });
      if (items.length === 0) setState({ loading: false, error: null, none: true });
      else onImport(items, picked.name);
    } catch {
      setState({ loading: false, error: 'Couldn’t load that tour’s items — try again', none: false });
    }
  };

  return (
    <Popover
      open
      anchorEl={anchor}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{ paper: { sx: { p: 1.5, width: 380, maxWidth: '90vw', display: 'grid', gap: 1 } } }}
    >
      <Typography variant="body2">
        Adds the other tour’s items that this tour does not have yet, visible to all users. Nothing is saved until you
        save.
      </Typography>
      <RemoteAutocomplete<TourListItem>
        label="From which tour"
        errorText="Couldn’t load the operator’s tours"
        size="small"
        fetchOnEmpty
        value={picked}
        getOptionLabel={(t) => t.name}
        getOptionKey={(t) => t.id}
        getOptionMeta={(t) => ({
          name: t.name,
          pk: t.id,
          tenant: t.tenant?.code ?? null,
          extra: [t.is_bespoke ? (t.start_date ?? null) : 'Recurring'],
        })}
        queryFn={async (q) => {
          const r = await toursApi.list({ tour_operator_id: detail.tenant!.id, pageSize: 25, ...(q.trim() ? { search: q.trim() } : {}) });
          return r.results.filter((t) => t.id !== detail.id);
        }}
        onChange={(v) => {
          setPicked(v as TourListItem | null);
          setState({ loading: false, error: null, none: false });
        }}
      />
      {state.error && (
        <Typography variant="caption" color="error" sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <ErrorOutlineIcon aria-hidden sx={{ fontSize: 16 }} />
          {state.error}
        </Typography>
      )}
      {state.none && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <InfoOutlinedIcon aria-hidden sx={{ fontSize: 16 }} />
          That tour has no items this tour lacks.
        </Typography>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button size="small" variant="contained" disabled={!picked || state.loading} onClick={() => void run()}>
          {state.loading ? 'Loading…' : 'Add its items'}
        </Button>
      </Box>
    </Popover>
  );
}

// px strings: in sx a bare `1` is 100%.
const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  m: '-1px',
  p: 0,
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;
